import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { AdminIcon } from "@/components/admin/icon";
import type { Category } from "@/lib/catalog/types";

interface CategoryPickerProps {
	/** Liste plate des catégories (avec parentId / level). */
	categories: Category[];
	/** Id de la catégorie sélectionnée. */
	value: string;
	onChange: (id: string) => void;
	placeholder?: string;
	/** data-testid du déclencheur (pour les tests). */
	testId?: string;
	/**
	 * Rendu inline (recherche + arbre toujours visibles, pas de dropdown flottant).
	 * À utiliser dans une modale : un panneau en position absolute y serait rogné
	 * par l'overflow de la boîte.
	 */
	inline?: boolean;
}

/**
 * Sélecteur de catégorie filtrable + en accordéon (single-select).
 * Pensé pour de gros catalogues : champ de recherche qui filtre l'arbre en
 * direct (avec ancêtres + dépliage auto), sinon arbre repliable niveau par
 * niveau. Sélection possible à n'importe quel niveau.
 */
export function CategoryPicker({
	categories,
	value,
	onChange,
	placeholder = "— Sélectionner une catégorie —",
	testId,
	inline = false,
}: CategoryPickerProps) {
	const [open, setOpen] = useState(false);
	const [query, setQuery] = useState("");
	const [expanded, setExpanded] = useState<Set<string>>(new Set());
	const rootRef = useRef<HTMLDivElement>(null);

	const byId = useMemo(
		() => new Map(categories.map((c) => [c.id, c])),
		[categories],
	);

	const childrenByParent = useMemo(() => {
		const m = new Map<string, Category[]>();
		for (const c of categories) {
			if (!c.parentId) continue;
			const arr = m.get(c.parentId) ?? [];
			arr.push(c);
			m.set(c.parentId, arr);
		}
		for (const arr of m.values()) {
			arr.sort(
				(a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
			);
		}
		return m;
	}, [categories]);

	const roots = useMemo(() => {
		const present = new Set(categories.map((c) => c.id));
		return categories
			.filter((c) => !c.parentId || !present.has(c.parentId))
			.sort(
				(a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name),
			);
	}, [categories]);

	// Fil d'Ariane de la catégorie sélectionnée (racine → feuille).
	const selectedPath = useMemo(() => {
		const path: Category[] = [];
		let cur = value ? byId.get(value) : undefined;
		let guard = 0;
		while (cur && guard++ < 12) {
			path.unshift(cur);
			cur = cur.parentId ? byId.get(cur.parentId) : undefined;
		}
		return path;
	}, [value, byId]);

	// Recherche : nœuds correspondants + leurs ancêtres (pour le contexte), dépliés.
	const q = query.trim().toLowerCase();
	const search = useMemo(() => {
		if (!q) return null;
		const visible = new Set<string>();
		const forceExpand = new Set<string>();
		for (const c of categories) {
			if (!c.name.toLowerCase().includes(q)) continue;
			visible.add(c.id);
			let p = c.parentId ? byId.get(c.parentId) : undefined;
			let guard = 0;
			while (p && guard++ < 12) {
				visible.add(p.id);
				forceExpand.add(p.id);
				p = p.parentId ? byId.get(p.parentId) : undefined;
			}
		}
		return { visible, forceExpand };
	}, [q, categories, byId]);

	useEffect(() => {
		if (!open) return;
		const onDoc = (e: MouseEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node))
				setOpen(false);
		};
		document.addEventListener("mousedown", onDoc);
		return () => document.removeEventListener("mousedown", onDoc);
	}, [open]);

	// Déplie automatiquement le chemin vers la catégorie sélectionnée (une seule
	// fois, quand les catégories sont chargées) — l'utilisateur voit où il en est.
	useEffect(() => {
		if (!value || categories.length === 0) return;
		setExpanded((prev) => {
			if (prev.size > 0) return prev;
			const next = new Set(prev);
			let cur = byId.get(value);
			let guard = 0;
			while (cur?.parentId && guard++ < 12) {
				next.add(cur.parentId);
				cur = byId.get(cur.parentId);
			}
			return next;
		});
	}, [value, categories.length, byId]);

	const toggle = (id: string) =>
		setExpanded((s) => {
			const n = new Set(s);
			if (n.has(id)) n.delete(id);
			else n.add(id);
			return n;
		});

	const isExpanded = (id: string) =>
		search ? search.forceExpand.has(id) : expanded.has(id);

	const select = (id: string) => {
		onChange(id);
		setOpen(false);
		setQuery("");
	};

	const renderNode = (c: Category, depth: number): ReactNode => {
		if (search && !search.visible.has(c.id)) return null;
		const kids = childrenByParent.get(c.id) ?? [];
		const hasKids = kids.length > 0;
		const selected = c.id === value;
		return (
			<div key={c.id}>
				<div
					style={{
						display: "flex",
						alignItems: "center",
						gap: 4,
						padding: "4px 6px",
						paddingLeft: 6 + depth * 16,
						background: selected ? "var(--a-surface-2)" : undefined,
						borderRadius: 4,
					}}
				>
					{hasKids ? (
						<button
							type="button"
							onClick={() => toggle(c.id)}
							aria-label={isExpanded(c.id) ? "Replier" : "Déplier"}
							style={{
								display: "inline-flex",
								alignItems: "center",
								justifyContent: "center",
								width: 18,
								height: 18,
								border: "none",
								background: "transparent",
								cursor: "pointer",
								color: "var(--a-muted)",
							}}
						>
							<AdminIcon n={isExpanded(c.id) ? "chevD" : "chevR"} s={12} />
						</button>
					) : (
						<span style={{ width: 18, display: "inline-block" }} />
					)}
					<button
						type="button"
						onClick={() => select(c.id)}
						data-testid={`cat-pick-${c.id}`}
						style={{
							flex: 1,
							textAlign: "left",
							border: "none",
							background: "transparent",
							cursor: "pointer",
							fontSize: 13,
							fontWeight: selected ? 600 : 400,
							color: "inherit",
							padding: "2px 0",
						}}
					>
						{c.iconEmoji ? `${c.iconEmoji} ` : ""}
						{c.name}
					</button>
					{selected && <AdminIcon n="check" s={14} c="var(--a-primary)" />}
				</div>
				{hasKids &&
					isExpanded(c.id) &&
					kids.map((k) => renderNode(k, depth + 1))}
			</div>
		);
	};

	const panel = (listMaxHeight: number) => (
		<>
			<div
				style={{
					padding: 8,
					borderBottom: "1px solid var(--a-border)",
					position: "relative",
				}}
			>
				<span
					style={{
						position: "absolute",
						left: 16,
						top: 16,
						color: "var(--a-muted)",
					}}
				>
					<AdminIcon n="search" s={14} />
				</span>
				<input
					autoFocus
					className="aform-i"
					placeholder="Filtrer une catégorie…"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					data-testid="cat-pick-search"
					style={{ paddingLeft: 32 }}
				/>
			</div>
			<div style={{ maxHeight: listMaxHeight, overflow: "auto", padding: 6 }}>
				{roots.map((c) => renderNode(c, 0))}
				{search && search.visible.size === 0 && (
					<div className="muted" style={{ fontSize: 12, padding: "8px 6px" }}>
						Aucune catégorie ne correspond à « {query} ».
					</div>
				)}
			</div>
		</>
	);

	// Mode inline (modales) : sélection courante + panneau toujours visible, en flux
	// normal — rien ne dépasse de la boîte, donc rien n'est rogné.
	if (inline) {
		return (
			<div ref={rootRef} data-testid={testId}>
				<div
					style={{
						fontSize: 12.5,
						marginBottom: 8,
						color: selectedPath.length ? "var(--a-ink-2)" : "var(--a-muted)",
					}}
				>
					{selectedPath.length ? (
						<>
							Sélection :{" "}
							<strong>{selectedPath.map((c) => c.name).join(" › ")}</strong>
						</>
					) : (
						placeholder
					)}
				</div>
				<div
					style={{
						background: "var(--a-surface)",
						border: "1px solid var(--a-border)",
						borderRadius: 8,
						overflow: "hidden",
					}}
				>
					{panel(280)}
				</div>
			</div>
		);
	}

	return (
		<div ref={rootRef} style={{ position: "relative" }}>
			<button
				type="button"
				className="aform-i"
				data-testid={testId}
				onClick={() => setOpen((o) => !o)}
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					width: "100%",
					textAlign: "left",
					cursor: "pointer",
					gap: 8,
				}}
			>
				<span
					style={{
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap",
						color: selectedPath.length ? "inherit" : "var(--a-muted)",
					}}
				>
					{selectedPath.length
						? selectedPath.map((c) => c.name).join(" › ")
						: placeholder}
				</span>
				<AdminIcon n="chevD" s={14} c="var(--a-muted)" />
			</button>

			{open && (
				<div
					style={{
						position: "absolute",
						zIndex: 50,
						top: "calc(100% + 4px)",
						left: 0,
						right: 0,
						background: "var(--a-surface)",
						border: "1px solid var(--a-border)",
						borderRadius: 8,
						boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
						overflow: "hidden",
					}}
				>
					{panel(320)}
				</div>
			)}
		</div>
	);
}
